import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
} from '@chakra-ui/react';

import { WithTooltip } from '@revelio/core';

import { ThemedStatusTag } from '../../shared/ui/table/themed-status-tag';
import { useIsThemedAppearance } from '../../shared/user-preferences/hooks/useIsThemedAppearance';
import type {
  DataBuilderStatus as DataBuilderApiStatus,
  DataBuilderPayload,
  DataBuilderStatusGetResponse,
} from './api/data-builder-api.types';
import { DataBuilderConfigSummary } from './DataBuilderConfigSummary';
import {
  dataBuilderTagStatusColorMap,
  dataBuilderTagStatusTone,
  defaultTagColors,
  FINISHED_WITH_EMPTY_TOOLTIP,
  getDataBuilderStatusLabel,
  isFinishedWithEmptyStatus,
} from './DataBuilderStatus';
import modalStyles from './data-builder-modals.module.css';

type DataBuilderStatusDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  client: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
  snowflake_tables: DataBuilderStatusGetResponse[number]['snowflake_tables'];
  s3_paths: DataBuilderStatusGetResponse[number]['s3_paths'];
};

export const DataBuilderStatusDetailsModal = ({
  isOpen,
  onClose,
  name,
  client,
  status,
  url,
  payload,
  snowflake_tables,
  s3_paths,
}: DataBuilderStatusDetailsModalProps) => {
  const palette = dataBuilderTagStatusColorMap[status] ?? defaultTagColors;
  const isThemedAppearance = useIsThemedAppearance();

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        className={modalStyles.modalContent}
        minHeight="400px"
        maxHeight="calc(100vh - 4rem)"
      >
        <ModalHeader
          className={modalStyles.modalHeader}
          borderBottom="1px solid #E5EBF1"
          fontSize="17px"
          fontWeight="600"
          py="12px"
        >
          <Flex alignItems="center" gap={3}>
            <span>
              {client} - {name}
            </span>
            <WithTooltip
              showCondition={isFinishedWithEmptyStatus(status)}
              label={FINISHED_WITH_EMPTY_TOOLTIP}
              placement="bottom"
            >
              {isThemedAppearance ? (
                <ThemedStatusTag
                  tone={dataBuilderTagStatusTone[status] ?? 'neutral'}
                >
                  {getDataBuilderStatusLabel(status)}
                </ThemedStatusTag>
              ) : (
                <Tag
                  bg={palette.bgColor}
                  color={palette.color}
                  borderRadius="4px"
                  fontWeight="semibold"
                  minWidth="94px"
                  textAlign="center"
                  py={1}
                  justifyContent="center"
                  cursor="default"
                >
                  {getDataBuilderStatusLabel(status)}
                </Tag>
              )}
            </WithTooltip>
          </Flex>
        </ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody py="16px">
          <DataBuilderConfigSummary
            payload={payload}
            status={status}
            url={url}
            snowflakeTables={snowflake_tables}
            s3Paths={s3_paths}
            active={isOpen}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
